// react
// dependencies
import { List, Map, fromJS } from "immutable";
import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";

import Button from "../../components/Button";
// components
import Icon from "../../components/Icon";
import TextInput from "../../components/Inputs/TextInput";
// config
import Config from "./Config";
import AddMembers from "./components/AddMembers";
import { RemoveUserFromGroupButton } from "./components/RemoveUserFromGroupButton";

// actions
import {
	changeScreen,
	deleteGroup,
	initializeUI,
	makeGroupNameEditable,
	removeEmployeeFromGroup,
	sendNewGroupName,
	setColumns,
	updateEditingGroupName,
} from "./actions";

import { getAppHeight, getAppWidth, getUserIsAdmin } from "../../selectors/app";
// selectors
import { getCompanySettingsContext } from "../../selectors/settings";
import {
	getActiveScreen,
	getEditingGroupName,
	getGroupId,
	getGroupObject,
	getPageUISelector,
	getUserIsAGroupAdmin,
} from "./selectors";

import Administrators from "./screens/Administrators";
// screens
import Employees from "./screens/Members";
import Permissions from "./screens/Permissions";

export class GroupPage extends React.Component {
	/*
        The page for creating a review cycle.
    */

	static propTypes = {
		appWidth: PropTypes.number,
		appHeight: PropTypes.number,
		userIsAdmin: PropTypes.bool.isRequired,
		userIsAGroupAdmin: PropTypes.bool.isRequired,
		isNameEditable: PropTypes.bool.isRequired,
		initialize: PropTypes.func.isRequired,
		changeScreen: PropTypes.func.isRequired,
		setColumns: PropTypes.func.isRequired,
		removeEmployeeFromGroup: PropTypes.func.isRequired,
		makeGroupNameEditable: PropTypes.func.isRequired,
		sendNewGroupName: PropTypes.func.isRequired,
		updateEditingGroupName: PropTypes.func.isRequired,
		deleteGroup: PropTypes.func.isRequired,
		groupId: PropTypes.string.isRequired,
		groupName: PropTypes.string.isRequired,
		editingGroupName: PropTypes.string.isRequired,
		activeScreen: PropTypes.oneOf(["MEMBERS", "ADMINS", "PERMISSIONS"]).isRequired,
		companyFeatures: PropTypes.instanceOf(Map).isRequired,
	};

	static defaultProps = {
		appHeight: 800,
		appWidth: 800,
	};

	constructor(props) {
		super(props);
		const { groupId } = this.props;

		props.initialize(groupId);

		// setup the columns
		const tableColumns = [
			{
				name: "Name",
				_id: "_displayName",
				type: "_displayName",
				isActive: true,
			},
			{
				name: "",
				_id: "_remove",
				type: "custom",
				cell: ({ userId, user }) => {
					return (
						<RemoveUserFromGroupButton
							handleRemoveEmployee={() => this.handleRemoveEmployee(userId)}
							userId={userId}
							isHrisLocked={user.hris_locked && props.activeScreen === "MEMBERS"}
						/>
					);
				},
			},
		];

		props.setColumns(tableColumns);
	}

	handleRemoveEmployee = (employeeID) => {
		this.props.removeEmployeeFromGroup(employeeID, this.props.activeScreen);
	};

	handlePageChange = (screen) => {
		this.props.changeScreen(screen);
	};

	makeNameEditable = () => {
		if (this.props.userIsAdmin || this.props.userIsAGroupAdmin) {
			this.props.makeGroupNameEditable(true);
		}
	};

	cancelSaveGroupName = () => {
		this.props.makeGroupNameEditable(false);
	};

	handleEditGroupName = (value) => {
		this.props.updateEditingGroupName(value);
	};

	handleSaveGroupName = () => {
		this.props.sendNewGroupName();
	};

	handleDeleteGroup = () => {
		this.props.deleteGroup();
	};

	render() {
		const {
			appWidth,
			appHeight,
			groupName,
			userIsAdmin,
			activeScreen,
			isNameEditable,
			editingGroupName,
			userIsAGroupAdmin,
			companyFeatures,
		} = this.props;

		const goalsEnabled = companyFeatures.getIn(["goals", "state"], "enabled") === "enabled";
		const reviewsEnabled = companyFeatures.getIn(["reviews", "state"], "enabled") === "enabled";

		const pageProps = {
			userIsAdmin,
			userIsAGroupAdmin,
			screenHeight: appHeight - (Config.pageWrapperSpacing + 50),
			screenWidth: appWidth - (Config.menuWidth + Config.spacingMenuToScreen + Config.pageWrapperSpacing * 2),
		};

		const groupNameElement = isNameEditable ? (
			<div className="editable-group-name" style={{ maxWidth: 500 }}>
				<div style={{ border: "1px solid #ddd" }}>
					<div style={{ padding: 10 }}>
						<TextInput onChange={this.handleEditGroupName} value={editingGroupName} />
						<div style={{ marginTop: 10 }}>
							<Button
								onClick={this.handleSaveGroupName}
								style={{ display: "inline-block" }}
								color="green"
							>
								Save
							</Button>
							<Button
								onClick={this.cancelSaveGroupName}
								style={{
									display: "inline-block",
									marginLeft: 10,
								}}
								color="grey"
							>
								Cancel
							</Button>
						</div>
					</div>
				</div>
			</div>
		) : (
			<div className="group-name" onClick={this.makeNameEditable}>
				<div className="name-container">
					<span>{groupName}</span>
					{userIsAdmin || userIsAGroupAdmin ? <Icon size={12} name="pencil" /> : ""}
				</div>
			</div>
		);

		return (
			<section className="page-view-group" style={{}}>
				<section
					className="page-wrapper"
					style={{
						margin: `0 ${Config.pageWrapperSpacing}px`,
						paddingTop: Config.pageWrapperSpacing,
					}}
				>
					<section
						className="page-menu-navigation"
						style={{
							top: Config.pageWrapperSpacing,
							width: Config.menuWidth - 20,
						}}
					>
						{userIsAdmin ? (
							<a href="/administration/groups/" className="back-to-groups-button">
								<Icon type="fa5" name={["far", "users"]} size="lg" />
								<span>Back to Groups</span>
							</a>
						) : (
							""
						)}

						<div className="nav-buttons">
							<div>
								<div
									onClick={() => this.handlePageChange("MEMBERS")}
									className={`menu-item-wrapper ${activeScreen === "MEMBERS" ? "is-active" : ""}`}
								>
									<div className="item">Members</div>
								</div>
								{companyFeatures && (goalsEnabled || reviewsEnabled) ? (
									<div
										onClick={() => this.handlePageChange("ADMINS")}
										className={`menu-item-wrapper ${activeScreen === "ADMINS" ? "is-active" : ""}`}
									>
										<div className="item">Admins</div>
									</div>
								) : (
									<div />
								)}
								{companyFeatures && (goalsEnabled || reviewsEnabled) ? (
									<div
										onClick={() => this.handlePageChange("PERMISSIONS")}
										className={`menu-item-wrapper ${
											activeScreen === "PERMISSIONS" ? "is-active" : ""
										}`}
									>
										<div className="item">Permissions</div>
									</div>
								) : (
									<div />
								)}
							</div>
						</div>

						{userIsAdmin ? (
							<div className="delete-group-wrapper">
								<Button onClick={this.handleDeleteGroup} className="delete-group-button" color="red">
									Delete Group
								</Button>
							</div>
						) : (
							""
						)}
					</section>

					<section
						className="page-body-container"
						style={{
							marginLeft: Config.menuWidth + Config.spacingMenuToScreen,
						}}
					>
						<section className="group-head">{groupNameElement}</section>
						<div>
							<section className="screen-wrapper">
								<Employees isActive={activeScreen === "MEMBERS"} {...pageProps} />
								<Administrators isActive={activeScreen === "ADMINS"} {...pageProps} />
								<Permissions isActive={activeScreen === "PERMISSIONS"} {...pageProps} />
							</section>
						</div>
					</section>
				</section>

				<AddMembers />
			</section>
		);
	}
}

const mapStateToProps = (state) => {
	const group = getGroupObject(state);
	const pageUI = getPageUISelector(state);

	return {
		groupId: getGroupId(state),
		appWidth: getAppWidth(state),
		appHeight: getAppHeight(state),
		groupName: group.get("name", ""),
		userIsAdmin: getUserIsAdmin(state),
		activeScreen: getActiveScreen(state),
		editingGroupName: getEditingGroupName(state),
		userIsAGroupAdmin: getUserIsAGroupAdmin(state),
		companyFeatures: getCompanySettingsContext(state),
		isNameEditable: pageUI.get("isGroupNameEditable", false),
	};
};

const mapDispatchToProps = (dispatch) => ({
	sendNewGroupName: () => dispatch(sendNewGroupName()),
	deleteGroup: () => dispatch(deleteGroup(false)),
	changeScreen: (screen) => dispatch(changeScreen(screen)),
	setColumns: (columns) => dispatch(setColumns(columns)),
	initialize: (groupId) => dispatch(initializeUI(groupId)),
	updateEditingGroupName: (value) => dispatch(updateEditingGroupName(value)),
	makeGroupNameEditable: (isEditable) => dispatch(makeGroupNameEditable(isEditable)),
	removeEmployeeFromGroup: (employeeID, membershipType) =>
		dispatch(removeEmployeeFromGroup(employeeID, membershipType)),
});

export default connect(mapStateToProps, mapDispatchToProps)(GroupPage);
